import { useState, useEffect } from 'react';
import { PageLayout, DetailsDrawer } from 'shared-ui';

import { ReactComponent as CollapseIcon } from 'assets/icons/collapse.svg';
import { ReactComponent as ExpandIcon } from 'assets/icons/expand.svg';
import { findAllByKey } from 'helpers/findAllByKey';
import { Loader } from 'ui/loader/Loader';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useSelectedOrganization } from 'hooks/useSelectedOrganization/useSelectedOrganization';
import { DefinitionsBar } from '../definitionsBar/DefinitionsBar';
import { getObjectInNestedObjectByKey } from 'helpers/getObjectInNestedObjectByKey';

import * as Styled from './Organization.styles';
import { OrganizationHeader } from './organizationHeader/OrganizationHeader';
import { OrganizationItem } from './organizationItem/OrganizationItem';
import { OrganizationProps } from './Organization.types';
import { OrganizationDetailsContainer } from './organizationDetails/OrganizationDetailsContainer';
import { EXPAND_ORGANIZATION_ITEM_TAGS } from './Organization.variables';

export const Organization = ({ isLoadingOrganization, organizations, setOrganizations }: OrganizationProps) => {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [searchOrganizationId, setSearchOrganizationId] = useState<string>();
  const { formatMessage } = useLocale();
  const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();

  const allNodeIds = organizations ? organizations.flatMap((org) => findAllByKey(org, 'id')) : [];
  const isAllNodesExpanded = expanded.length === allNodeIds.length;

  useEffect(() => {
    if (!organizations || !searchOrganizationId) return;

    let updatedSelectedOrganization = null;

    // Search in all organizations
    for (const organization of organizations) {
      const found = getObjectInNestedObjectByKey(organization, 'id', searchOrganizationId);
      if (found) {
        updatedSelectedOrganization = found;
        break;
      }
    }

    if (!updatedSelectedOrganization) return;

    setSelectedOrganization(updatedSelectedOrganization.object);
    setExpanded(updatedSelectedOrganization.parentIds);
  }, [searchOrganizationId, organizations]);

  const handleToggle = (_: React.SyntheticEvent, nodeIds: string[]) => {
    if (EXPAND_ORGANIZATION_ITEM_TAGS.includes((_.target as HTMLElement)?.tagName)) {
      setExpanded(nodeIds);
    }
  };

  const handleToggleAll = () => {
    setExpanded(isAllNodesExpanded ? [] : allNodeIds);
  };

  return (
    <PageLayout
      header={formatMessage({ id: AppMessages['definitions.title'] })}
      renderContent={() => (
        <>
          <DefinitionsBar
            searchOrganizationId={searchOrganizationId}
            setSearchOrganizationId={setSearchOrganizationId}
          />
          {isLoadingOrganization ? (
            <Loader />
          ) : (
            <>
              <OrganizationHeader isAllNodesExpanded={isAllNodesExpanded} handleToggleAll={handleToggleAll} />
              <Styled.TreeView
                selected={selectedOrganization?.id || ''}
                expanded={expanded}
                aria-label="organizations-label"
                defaultCollapseIcon={<ExpandIcon />}
                defaultExpandIcon={<CollapseIcon />}
                onNodeToggle={handleToggle}
              >
                {organizations?.map((organization) => (
                  <OrganizationItem key={organization.id} mainOrganization={organization} organization={organization} />
                ))}
              </Styled.TreeView>
            </>
          )}
        </>
      )}
      renderDetails={() => (
        <DetailsDrawer
          isOpen={!!selectedOrganization}
          header={formatMessage({ id: AppMessages['event.details.title'] })}
        >
          {organizations &&
            selectedOrganization &&
            (() => {
              // Find the main organization that contains the selected organization
              const mainOrganization = organizations.find((org) => {
                if (org.id === selectedOrganization.id) {
                  return true;
                }
                return getObjectInNestedObjectByKey(org, 'id', selectedOrganization.id);
              });

              return mainOrganization ? (
                <OrganizationDetailsContainer
                  mainOrganization={mainOrganization}
                  selectedOrganization={selectedOrganization}
                  setOrganizations={setOrganizations}
                  organizations={organizations}
                />
              ) : null;
            })()}
        </DetailsDrawer>
      )}
    />
  );
};
